import React from 'react';
import type { FormInstance } from 'antd/es/form';
import { Modal, message, Form, Input, Select, Button, Spin } from 'antd';
import { CloseOutlined, FileTextOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import CommonTable from 'components/CommonTable';
import type {
    CoverSheetTemplate,
    FaxCoverPageTemplateVo,
    CoverPageResponse,
    TFaxCoverPageTemplateReq,
} from 'types/eFax';
import {
    faxCoverPageTemplateCreate,
    faxCoverPageTemplateList,
    faxCoverPageTemplateDeleteById,
    faxCoverPageTemplateUpdate,
    faxDocumoList,
} from 'api/fax';
import { isFaxScheduleSuccess } from 'utils/eFaxScheduleRows';
import { formatUTCTime } from 'utils/common';
import s from '../s.module.less';

type TemplateFormValues = {
    note?: string;
    subject?: string;
    tag?: string[];
    templateName: string;
};

const TAG_OPTIONS = [
    'Prior Authorization',
    'Pharmacy',
    'Referral',
    'General',
    'Clinical Notes',
    'Labs & Imaging',
].map((v) => ({ label: v, value: v }));

const PAGE_SIZE = 20;

const createLocalTemplateId = () => {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        return crypto.randomUUID();
    }
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const mapTemplateVoToRow = (vo: FaxCoverPageTemplateVo): CoverSheetTemplate => ({
    id: String(vo.id ?? createLocalTemplateId()),
    name: vo.templateName || '',
    subject: vo.subject || undefined,
    note: vo.note || undefined,
    documoCoverpageId: vo.documoCoverpageId,
    tag: vo.tag?.length ? vo.tag.join(', ') : undefined,
    lastModified: vo.updateAt || vo.createAt || new Date().toISOString(),
    thumbnailUrl: vo.previewLink || undefined,
});

type CoverTemplateModalBodyProps = {
    documoLoading: boolean;
    documoTemplates: CoverPageResponse[];
    form: FormInstance<TemplateFormValues>;
    onSelectDocumo: (id: string | null) => void;
    previewFallbackUrl?: string | null;
    selectedDocumoId: string | null;
};

const CoverTemplateModalBody: React.FC<CoverTemplateModalBodyProps> = ({
    documoLoading,
    documoTemplates,
    form,
    onSelectDocumo,
    previewFallbackUrl,
    selectedDocumoId,
}) => {
    const selectedTemplate = documoTemplates.find(
        (d) => (d.uuid || String(d.id)) === selectedDocumoId,
    );
    const selectedTemplateHtml =
        typeof selectedTemplate?.template === 'string' ? selectedTemplate.template : undefined;

    const previewHtmlDoc = React.useMemo(() => {
        if (!selectedTemplateHtml) return undefined;
        const body = selectedTemplateHtml;
        return `<!doctype html>
<html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width,initial-scale=1" />
    <style>
      html, body { margin: 0; padding: 0; background: #fff; }
      body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", "Helvetica Neue", Arial, sans-serif; }
      img { max-width: 100%; height: auto; }
    </style>
  </head>
  <body>${body}</body>
</html>`;
    }, [selectedTemplateHtml]);
    const previewSrc =
        selectedTemplate?.previewLink || selectedTemplate?.previewUrl || previewFallbackUrl || undefined;
    const previewNode = previewHtmlDoc ? (
        <iframe
            title="Template preview"
            className={s.a4PreviewIframe}
            srcDoc={previewHtmlDoc}
            sandbox=""
            referrerPolicy="no-referrer"
        />
    ) : previewSrc ? (
        <img src={previewSrc} alt="Template preview" className={s.a4PreviewImage} />
    ) : null;

    return (
        <div className={s.modalBody}>
            <Form form={form} layout="vertical">
                <div className={s.twoColRow}>
                    <div className={s.formCol}>
                        <Form.Item
                            label="Cover sheet template name"
                            name="templateName"
                            rules={[{ required: true, message: 'Please enter template name' }]}
                            className={s.formItemNoMargin}
                        >
                            <Input maxLength={100} />
                        </Form.Item>
                    </div>
                    <div className={s.formCol}>
                        <Form.Item
                            label="Template type (Optional)"
                            name="tag"
                            className={s.formItemNoMargin}
                        >
                            <Select mode="tags" placeholder="" options={TAG_OPTIONS} />
                        </Form.Item>
                    </div>
                </div>

                {documoLoading ? (
                    <div className={s.spinnerCenter}>
                        <Spin size="small" />
                    </div>
                ) : documoTemplates.length > 0 ? (
                    <div className={s.templateStylesRow}>
                        {documoTemplates.map((t) => {
                            const id = t.uuid || String(t.id);
                            const isSelected = selectedDocumoId === id;
                            const preview = t.previewLink || t.previewUrl;
                            return (
                                <div
                                    key={id}
                                    className={s.templateStyleItem}
                                    onClick={() => onSelectDocumo(isSelected ? null : id)}
                                >
                                    <div
                                        className={`${s.templateStyleThumb}${isSelected ? ` ${s.templateStyleThumbSelected}` : ''}`}
                                    >
                                        {preview ? (
                                            <img src={preview} alt={t.name || ''} />
                                        ) : (
                                            <FileTextOutlined className={s.templateThumbIcon} />
                                        )}
                                    </div>
                                    <span className={s.templateStyleLabel}>{t.name || id}</span>
                                </div>
                            );
                        })}
                    </div>
                ) : null}

                <div className={s.a4Preview}>
                    {previewNode}
                    <div className={s.a4PreviewFields}>
                        <div className={s.a4FieldRow}>
                            <span className={s.a4FieldLabel}>Subject</span>
                            <Form.Item name="subject" className={s.formItemNoMargin}>
                                <Input maxLength={200} />
                            </Form.Item>
                        </div>
                        <div className={s.a4FieldRow}>
                            <span className={s.a4FieldLabel}>Remarks</span>
                            <Form.Item name="note" className={s.formItemNoMargin}>
                                <Input.TextArea rows={4} placeholder="Remarks here" maxLength={1000} />
                            </Form.Item>
                        </div>
                    </div>
                </div>
            </Form>
        </div>
    );
};

const CoverSheetTab: React.FC = () => {
    const [form] = Form.useForm<TemplateFormValues>();
    const [editForm] = Form.useForm<TemplateFormValues>();
    const [templates, setTemplates] = React.useState<CoverSheetTemplate[]>([]);
    const [listLoading, setListLoading] = React.useState(false);
    const [currentPage, setCurrentPage] = React.useState(1);
    const [createOpen, setCreateOpen] = React.useState(false);
    const [creating, setCreating] = React.useState(false);
    const [editingId, setEditingId] = React.useState<string | null>(null);
    const [editOpen, setEditOpen] = React.useState(false);
    const [editing, setEditing] = React.useState(false);
    const [documoTemplates, setDocumoTemplates] = React.useState<CoverPageResponse[]>([]);
    const [documoLoading, setDocumoLoading] = React.useState(false);
    const [selectedDocumoId, setSelectedDocumoId] = React.useState<string | null>(null);
    const [editSelectedDocumoId, setEditSelectedDocumoId] = React.useState<string | null>(null);
    const [editPreviewFallbackUrl, setEditPreviewFallbackUrl] = React.useState<string | null>(null);

    const fetchTemplates = React.useCallback(async () => {
        setListLoading(true);
        try {
            const result = await faxCoverPageTemplateList({ current: 1, size: 100 });
            const body = result?.data as { code?: number; data?: { list?: FaxCoverPageTemplateVo[]; records?: FaxCoverPageTemplateVo[] } } | undefined;
            if (!result?.error && body && isFaxScheduleSuccess(body)) {
                const items = body.data?.list ?? body.data?.records ?? [];
                setTemplates(items.map(mapTemplateVoToRow));
            }
        } finally {
            setListLoading(false);
        }
    }, []);

    const fetchDocumoList = React.useCallback(async () => {
        setDocumoLoading(true);
        try {
            const result = await faxDocumoList();
            const body = result?.data as { code?: number; data?: CoverPageResponse[] } | undefined;
            if (!result?.error && body && isFaxScheduleSuccess(body)) {
                setDocumoTemplates(body.data ?? []);
            }
        } finally {
            setDocumoLoading(false);
        }
    }, []);

    React.useEffect(() => {
        fetchTemplates();
    }, [fetchTemplates]);

    const handleOpenCreate = () => {
        fetchDocumoList();
        setSelectedDocumoId(null);
        form.resetFields();
        setCreateOpen(true);
    };

    const handleCloseCreate = () => {
        setCreateOpen(false);
        form.resetFields();
        setSelectedDocumoId(null);
    };

    const handleDelete = (record: CoverSheetTemplate) => {
        Modal.confirm({
            title: 'Delete template',
            content: `Are you sure you want to delete "${record.name}"?`,
            okText: 'Delete',
            okButtonProps: { danger: true },
            onOk: async () => {
                const numId = Number(record.id);
                if (!Number.isFinite(numId)) {
                    await fetchTemplates();
                    message.success('Template deleted');
                    return;
                }
                const result = await faxCoverPageTemplateDeleteById(numId);
                const body = result?.data as { code?: number; message?: string } | undefined;
                const ok = !result?.error && (!body || isFaxScheduleSuccess(body));
                if (!ok) {
                    message.error(body?.message || 'Failed to delete template');
                    return;
                }
                message.success('Template deleted');
                await fetchTemplates();
            },
        });
    };

    const closeEditModal = () => {
        setEditOpen(false);
        editForm.resetFields();
        setEditingId(null);
        setEditSelectedDocumoId(null);
        setEditPreviewFallbackUrl(null);
    };

    const handleOpenEdit = (record: CoverSheetTemplate) => {
        fetchDocumoList();
        setEditingId(record.id);
        setEditSelectedDocumoId(record.documoCoverpageId ?? null);
        setEditPreviewFallbackUrl(record.thumbnailUrl ?? null);
        editForm.setFieldsValue({
            templateName: record.name,
            subject: record.subject,
            note: record.note,
            tag: record.tag ? record.tag.split(',').map((t) => t.trim()).filter(Boolean) : [],
        });
        setEditOpen(true);
    };

    const handleEditSubmit = async () => {
        try {
            const values = await editForm.validateFields();
            if (!editingId) return;
            const numId = Number(editingId);
            if (!Number.isFinite(numId)) {
                message.error('Invalid template id');
                return;
            }
            setEditing(true);
            const selectedEditTemplate = documoTemplates.find(
                (d) => (d.uuid || String(d.id)) === editSelectedDocumoId,
            );
            const previewFromDocumo =
                selectedEditTemplate?.previewLink || selectedEditTemplate?.previewUrl;
            const updatePayload: TFaxCoverPageTemplateReq = {
                templateName: values.templateName.trim(),
                subject: values.subject?.trim(),
                note: values.note?.trim(),
                tag: values.tag || [],
            };
            if (editSelectedDocumoId) {
                updatePayload.documoCoverpageId = editSelectedDocumoId;
                if (previewFromDocumo) updatePayload.previewLink = previewFromDocumo;
            }

            const result = await faxCoverPageTemplateUpdate(numId, updatePayload);
            const envelope = result?.data as { code?: number; message?: string; data?: FaxCoverPageTemplateVo } | undefined;
            if (result?.error || !envelope || !isFaxScheduleSuccess(envelope)) {
                message.error(envelope?.message || 'Failed to update template');
                return;
            }
            message.success('Template updated');
            closeEditModal();
            await fetchTemplates();
        } catch (error) {
            if ((error as { errorFields?: unknown })?.errorFields) return;
            message.error('Failed to update template');
        } finally {
            setEditing(false);
        }
    };

    const selectedTemplate = documoTemplates.find(
        (d) => (d.uuid || String(d.id)) === selectedDocumoId,
    );

    const handleCreateTemplate = async () => {
        try {
            const values = await form.validateFields();
            setCreating(true);
            const previewLink = selectedTemplate?.previewLink || selectedTemplate?.previewUrl;
            const result = await faxCoverPageTemplateCreate({
                documoCoverpageId: selectedDocumoId || undefined,
                isDefault: false,
                note: values.note?.trim(),
                previewLink: previewLink || undefined,
                subject: values.subject?.trim(),
                tag: values.tag || [],
                templateName: values.templateName.trim(),
            });
            const envelope = result?.data as
                | { code?: number; message?: string; data?: FaxCoverPageTemplateVo | null }
                | undefined;
            if (result?.error || !envelope || !isFaxScheduleSuccess(envelope) || !envelope.data) {
                const errText =
                    envelope?.message ||
                    (typeof result?.error === 'string' ? result.error : null) ||
                    'Failed to create cover sheet template';
                message.error(errText);
                return;
            }
            message.success('Cover sheet template created');
            handleCloseCreate();
            await fetchTemplates();
        } catch (error) {
            if ((error as { errorFields?: unknown })?.errorFields) return;
            message.error('Failed to create cover sheet template');
        } finally {
            setCreating(false);
        }
    };

    const columns: ColumnsType<CoverSheetTemplate> = [
        {
            title: 'NAME',
            dataIndex: 'name',
            render: (_, record) => (
                <div className={s.templateNameCell}>
                    <div className={s.templateThumb}>
                        {record.thumbnailUrl ? (
                            <img
                                src={record.thumbnailUrl}
                                alt=""
                                className={s.templateThumbImg}
                            />
                        ) : (
                            <FileTextOutlined />
                        )}
                    </div>
                    <div className={s.templateInfo}>
                        <span className={s.templateName}>{record.name}</span>
                        {record.subject && <span className={s.templateSubject}>{record.subject}</span>}
                    </div>
                </div>
            ),
        },
        {
            title: 'TAG',
            dataIndex: 'tag',
            width: 160,
            render: (tag: string) => (tag ? <span className={s.badgeTag}>{tag}</span> : '-'),
        },
        {
            title: 'LAST MODIFIED',
            dataIndex: 'lastModified',
            width: 200,
            sorter: (a, b) => new Date(a.lastModified).getTime() - new Date(b.lastModified).getTime(),
            render: (date: string) => (date ? formatUTCTime(date, 'MM/DD/YY') : '-'),
        },
        {
            title: '',
            key: 'delete',
            width: 80,
            render: (_, record) => (
                <span className={s.linkButton} onClick={() => handleDelete(record)}>
                    Delete
                </span>
            ),
        },
        {
            title: '',
            key: 'edit',
            width: 60,
            render: (_, record) => (
                <span className={s.linkButton} onClick={() => handleOpenEdit(record)}>
                    Edit
                </span>
            ),
        },
    ];

    return (
        <>
            <div className={s.actionBar}>
                <Button type="primary" onClick={handleOpenCreate}>
                    Create new cover sheet
                </Button>
            </div>
            <Spin spinning={listLoading}>
                <CommonTable<CoverSheetTemplate>
                    data={templates}
                    columns={columns}
                    rowKey="id"
                    pagination={{
                        current: currentPage,
                        pageSize: PAGE_SIZE,
                        total: templates.length,
                        onChange: setCurrentPage,
                        showSizeChanger: false,
                    }}
                    locale={{
                        emptyText: <div className={s.emptyText}>No cover sheet templates yet</div>,
                    }}
                />
            </Spin>

            {/* Create Modal */}
            <Modal
                open={createOpen}
                onCancel={handleCloseCreate}
                footer={null}
                width={780}
                className={s.createCoverSheetModal}
                styles={{ content: { padding: 0, overflow: 'hidden' }, body: { padding: 0 } }}
                destroyOnClose
                title={null}
                closable={false}
            >
                <div className={s.modalHeader}>
                    <span className={s.modalTitle}>Create new cover sheet</span>
                    <button type="button" className={s.modalCloseBtn} onClick={handleCloseCreate}>
                        <CloseOutlined />
                    </button>
                </div>

                <CoverTemplateModalBody
                    form={form}
                    documoTemplates={documoTemplates}
                    documoLoading={documoLoading}
                    selectedDocumoId={selectedDocumoId}
                    onSelectDocumo={setSelectedDocumoId}
                />

                <div className={s.modalFooter}>
                    <button type="button" className={s.cancelBtn} onClick={handleCloseCreate}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={s.primaryBtn}
                        disabled={creating}
                        onClick={handleCreateTemplate}
                    >
                        {creating ? 'Creating...' : 'Create'}
                    </button>
                </div>
            </Modal>

            {/* Edit Modal — same layout as create; no "default template" option */}
            <Modal
                open={editOpen}
                onCancel={closeEditModal}
                footer={null}
                width={780}
                className={s.createCoverSheetModal}
                styles={{ content: { padding: 0, overflow: 'hidden' }, body: { padding: 0 } }}
                destroyOnClose
                title={null}
                closable={false}
            >
                <div className={s.modalHeader}>
                    <span className={s.modalTitle}>Edit cover sheet template</span>
                    <button type="button" className={s.modalCloseBtn} onClick={closeEditModal}>
                        <CloseOutlined />
                    </button>
                </div>
                <CoverTemplateModalBody
                    form={editForm}
                    documoTemplates={documoTemplates}
                    documoLoading={documoLoading}
                    selectedDocumoId={editSelectedDocumoId}
                    onSelectDocumo={setEditSelectedDocumoId}
                    previewFallbackUrl={editPreviewFallbackUrl}
                />
                <div className={s.modalFooter}>
                    <button type="button" className={s.cancelBtn} onClick={closeEditModal}>
                        Cancel
                    </button>
                    <button
                        type="button"
                        className={s.primaryBtn}
                        disabled={editing}
                        onClick={handleEditSubmit}
                    >
                        {editing ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </Modal>
        </>
    );
};

export default CoverSheetTab;
